export type User = {
  id: string
  username: string
  displayName: string | null
  isOwner: boolean
}

export type FeatureMeta = {
  id: string
  pluginId: string
  name: string
  description: string
  icon: string
}

export type AdminUser = {
  id: string
  username: string
  displayName: string | null
  isActive: boolean
  isOwner: boolean
  createdAt: string
  lastLoginAt: string | null
}

export type AdminFeature = {
  id: string
  pluginId: string
  name: string
  description: string
  icon: string
  sortOrder: number
  isEnabled: boolean
}

export type AuditEntry = {
  id: number
  userId: string | null
  username: string | null
  action: string
  target: string | null
  ip: string | null
  meta: Record<string, unknown> | null
  createdAt: string
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const hasBody = init?.body !== undefined && init.body !== null
  const res = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    let parsed: string | undefined
    try {
      const j = JSON.parse(text) as { error?: string }
      parsed = j.error
    } catch {
      parsed = undefined
    }
    throw new ApiError(res.status, parsed ?? (text || res.statusText))
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
  }
}

export const api = {
  // ---- auth ----
  login: (username: string, password: string) =>
    request<{ user: User; features: FeatureMeta[] }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),
  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  me: () => request<{ user: User; features: FeatureMeta[] }>('/api/me'),
  changePassword: (oldPassword: string, newPassword: string) =>
    request<{ ok: true }>('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ oldPassword, newPassword }),
    }),

  // ---- admin: users ----
  listUsers: () => request<AdminUser[]>('/api/admin/users'),
  createUser: (input: { username: string; password: string; displayName?: string }) =>
    request<{ id: string }>('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateUser: (
    id: string,
    input: { displayName?: string | null; isActive?: boolean },
  ) =>
    request<{ id: string }>(`/api/admin/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  resetPassword: (id: string, newPassword: string) =>
    request<{ ok: true }>(`/api/admin/users/${id}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword }),
    }),
  deleteUser: (id: string) =>
    request<{ ok: true }>(`/api/admin/users/${id}`, { method: 'DELETE' }),

  // ---- admin: features ----
  listAdminFeatures: () => request<AdminFeature[]>('/api/admin/features'),
  patchFeature: (
    pluginId: string,
    input: {
      name?: string
      description?: string
      icon?: string
      sortOrder?: number
      isEnabled?: boolean
    },
  ) =>
    request<{ ok: true }>(`/api/admin/features/${pluginId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  // ---- admin: user feature assignments ----
  getUserFeatures: (userId: string) =>
    request<{ featureIds: string[] }>(`/api/admin/users/${userId}/features`),
  setUserFeatures: (userId: string, featureIds: string[]) =>
    request<{ ok: true }>(`/api/admin/users/${userId}/features`, {
      method: 'PUT',
      body: JSON.stringify({ featureIds }),
    }),

  // ---- admin: audit ----
  listAudit: (input: { limit?: number; action?: string } = {}) => {
    const q = new URLSearchParams()
    if (input.limit !== undefined) q.set('limit', String(input.limit))
    if (input.action) q.set('action', input.action)
    const qs = q.toString()
    return request<AuditEntry[]>(`/api/admin/audit${qs ? `?${qs}` : ''}`)
  },
}
